import React, { useCallback, useEffect, useState } from 'react';
import MessageBlockTreeNode from '../../../../types/BotBuilder/MessageBlockTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import SearchableDropdown, {
  DropdownItem
} from '../../../../components/SearchableDropdown/SearchableDropdown';
import UserContext from '../../../../context/UserContext/UserContext';
import ChatbotContext from '../../../../context/ChatbotContext/ChatbotContext';
import BotBuilderService from '../../../../services/BotBuilderService/BotBuilderService';

interface ResponseNodeSettingsProps {
  node: BotResponseTreeNode;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  isLocked?: boolean;
}

function ResponseNodeSettings({
  node,
  onNodeUpdate,
  isLocked = false
}: Readonly<ResponseNodeSettingsProps>): React.ReactElement {
  const messageNode = node.content as MessageBlockTreeNode;
  const [topics, setTopics] = useState<string[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const { user } = React.useContext(UserContext);
  const { currentChatbot } = React.useContext(ChatbotContext);

  const { category } = messageNode;

  const handleTopicsSearch = useCallback(
    (query: string, page: number): void => {
      if (!user || !currentChatbot) return;

      if (page === 0) {
        setTopicsLoading(true);
      }

      BotBuilderService.getTopics(currentChatbot.id)
        .then((res) => {
          const filteredTopics = res.topics.filter((topic: string) =>
            topic.toLowerCase().includes(query.toLowerCase())
          );
          setTopics(filteredTopics);
        })
        .catch(() => {
          setTopics([]);
        })
        .finally(() => {
          setTopicsLoading(false);
        });
    },
    [user, currentChatbot]
  );

  const handleTopicSelect = (item: DropdownItem): void => {
    messageNode.category = item.label;
    onNodeUpdate(node);
  };

  const handleTopicClear = (): void => {
    messageNode.category = null;
    onNodeUpdate(node);
  };

  const handleCustomValueCreate = (value: string): void => {
    messageNode.category = value;
    onNodeUpdate(node);
    setTopics((prev) => [...prev, value]);
  };

  useEffect(() => {
    if (user && currentChatbot) {
      handleTopicsSearch('', 0);
    }
  }, [user, currentChatbot, handleTopicsSearch]);

  const topicItems: DropdownItem[] = topics.map((topic) => ({
    id: topic,
    label: topic
  }));

  const selectedTopic = category ? { id: category, label: category } : null;

  return (
    <div className='flex flex-col items-center justify-center pb-4'>
      <div className='w-full'>
        <div className='mb-2'>
          <span className='text-sm font-medium text-white'>Response topic</span>
        </div>
        <SearchableDropdown
          items={topicItems}
          selectedItem={selectedTopic}
          onSelect={handleTopicSelect}
          onSearch={handleTopicsSearch}
          placeholder='Enter a topic'
          loading={topicsLoading}
          showClearButton
          onClear={handleTopicClear}
          allowCustomValue
          onCustomValueCreate={handleCustomValueCreate}
          className='w-full'
          triggerClassName='bg-white'
          disabled={isLocked}
        />
      </div>
    </div>
  );
}

export default ResponseNodeSettings;
